import { lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthGuard } from '@/features/auth';
import { ToastContainer, TooltipProvider } from '@/components/common';
import { MainLayout } from '@/features/layout/MainLayout';
import { SettingsScreen } from '@/features/settings/SettingsScreen';
import { PublicBriefPage } from '@/features/public-brief/PublicBriefPage';
import { LEADERSHIP_TAB_IDS } from '@/features/leadership-launchpad/leadership-paths';
import { PAGE_REGISTRY, USER_TAB_IDS } from '@/navigation/page-registry';
import { isRestrictedUser, landingTabForUser } from '@/navigation/access';
import { useAuthStore } from '@/stores/authStore';

const AiOsDevPreview = lazy(() =>
  import('@/features/ai-os').then((mod) => ({ default: mod.AiOsDevPreview })),
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function HomeHashRedirect() {
  const location = useLocation();

  if (location.pathname !== '/') return null;

  return (
    <Navigate
      to={{
        pathname: PAGE_REGISTRY.dashboard.path,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

/**
 * Keeps a restricted user on a page they are allowed to see.
 *
 * Without this they land on the dashboard, whose own API calls the access-group
 * middleware denies, and get a shell that looks broken -- the first thing a new
 * contractor would see. Renders nothing for unrestricted users, which is every
 * account with no access group.
 *
 * Deliberately inside AuthGuard: it needs the loaded user, and redirecting before
 * that would bounce everyone to the wrong place on first paint.
 */
function RestrictedUserRedirect() {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);

  if (!isRestrictedUser(user)) return null;

  const allowedPaths = (user?.allowed_page_keys ?? [])
    .map((key) => (PAGE_REGISTRY as Record<string, { path?: string }>)[key]?.path)
    .filter((path): path is string => Boolean(path));

  const onAllowedPage = allowedPaths.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );
  if (onAllowedPage) return null;

  const landingKey = landingTabForUser(user);
  const landingPath = landingKey
    ? (PAGE_REGISTRY as Record<string, { path?: string }>)[landingKey]?.path
    : undefined;
  // No resolvable page means the group grants nothing renderable. Staying put and
  // showing the denied state beats an infinite redirect loop.
  if (!landingPath) return null;

  return <Navigate to={landingPath} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        {/* Public routes — no auth required */}
        <Routes>
          <Route path="/b/:briefId" element={<PublicBriefPage />} />
          {import.meta.env.DEV ? (
            <Route
              path="/dev/ai-os"
              element={
                <Suspense fallback={null}>
                  <AiOsDevPreview />
                </Suspense>
              }
            />
          ) : null}
          <Route path="*" element={<AuthenticatedApp />} />
        </Routes>
      </HashRouter>
    </QueryClientProvider>
  );
}

function AuthenticatedApp() {
  return (
    <>
      <HomeHashRedirect />
      <AuthGuard>
        <RestrictedUserRedirect />
        <TooltipProvider delayDuration={200} skipDelayDuration={0}>
          <Routes>
            <Route path="/" element={<Navigate to={PAGE_REGISTRY.dashboard.path} replace />} />
            <Route path={PAGE_REGISTRY.dashboard.path} element={<MainLayout initialTab="dashboard" />} />
            <Route path="/okendo" element={<MainLayout initialTab="okendo" />} />
            <Route path="/chat" element={<Navigate to="/okendo" replace />} />
            <Route path="/conversations" element={<Navigate to="/gorgias" replace />} />
            <Route path="/gorgias" element={<MainLayout initialTab="gorgias" />} />
            <Route path="/kodif" element={<MainLayout initialTab="kodif" />} />
            <Route path="/flip" element={<MainLayout initialTab="flip" />} />
            <Route path="/trustpilot" element={<MainLayout initialTab="trustpilot" />} />
            <Route path="/google-business" element={<MainLayout initialTab="google-business" />} />
            <Route path="/pricing" element={<MainLayout initialTab="pricing" />} />
            <Route
              path="/operations/fulfillment/dashboard"
              element={<MainLayout initialTab="fulfillment" />}
            />
            <Route
              path="/operations/fulfillment/analytics"
              element={<MainLayout initialTab="fulfillment" />}
            />
            <Route
              path="/operations/fulfillment/exceptions"
              element={<MainLayout initialTab="fulfillment" />}
            />
            <Route
              path="/operations/split-ship"
              element={<Navigate to="/operations/split-ship/home" replace />}
            />
            <Route
              path="/operations/split-ship/:tab"
              element={<MainLayout initialTab="split-ship" />}
            />
            <Route
              path="/operations/cancellations"
              element={<MainLayout initialTab="operations-cancellations" />}
            />
            <Route
              path="/operations/inventory"
              element={<Navigate to="/operations/inventory/shipbob" replace />}
            />
            <Route
              path="/operations/inventory/:tab"
              element={<MainLayout initialTab="operations-inventory" />}
            />
            <Route
              path="/operations/oos-queue"
              element={<MainLayout initialTab="operations-oos-queue" />}
            />
            <Route
              path="/fulfillment"
              element={<Navigate to="/operations/fulfillment/dashboard" replace />}
            />
            <Route
              path="/fulfillment/analytics"
              element={<Navigate to="/operations/fulfillment/analytics" replace />}
            />
            <Route
              path="/fulfillment/exceptions"
              element={<Navigate to="/operations/fulfillment/exceptions" replace />}
            />
            <Route path="/cloudinary" element={<MainLayout initialTab="cloudinary" />} />
            <Route path="/gtm/*" element={<MainLayout initialTab="gtm" />} />
            <Route path="/forward-coverage" element={<MainLayout initialTab="forward-coverage" />} />
            <Route path="/replenishment" element={<MainLayout initialTab="replenishment" />} />
            <Route path="/calendar/*" element={<MainLayout initialTab="calendar" />} />
            <Route path="/milestones" element={<MainLayout initialTab="milestones" />} />
            <Route path="/roadshows/*" element={<MainLayout initialTab="roadshows" />} />
            <Route path="/roadshow-rep-performance" element={<MainLayout initialTab="roadshow-rep-performance" />} />
            <Route path="/planograms" element={<MainLayout initialTab="planograms" />} />
            <Route path="/wholesale/order-tracker/*" element={<MainLayout initialTab="shipbob-orders" />} />
            <Route path="/jobs" element={<Navigate to="/workflows" replace />} />
            <Route path="/workflows" element={<MainLayout initialTab="workflows" />} />
            <Route path="/studio/*" element={<MainLayout initialTab="studio" />} />
            <Route path="/media-studio/*" element={<MainLayout initialTab="studio" />} />
            <Route path="/video-studio/*" element={<MainLayout initialTab="studio" />} />
            <Route path="/image-studio" element={<MainLayout initialTab="studio" />} />
            <Route path={PAGE_REGISTRY.email.path} element={<MainLayout initialTab="email" />} />
            <Route path="/email" element={<Navigate to={PAGE_REGISTRY.email.path} replace />} />
            <Route path="/email-studio" element={<Navigate to={PAGE_REGISTRY.email.path} replace />} />
            {LEADERSHIP_TAB_IDS.map((tab) => (
              <Route
                key={`leadership-${tab}`}
                path={PAGE_REGISTRY[tab].path}
                element={<MainLayout initialTab={tab} />}
              />
            ))}
            {USER_TAB_IDS.map((tab) => (
              <Route
                key={`user-${tab}`}
                path={PAGE_REGISTRY[tab].path}
                element={<MainLayout initialTab={tab} />}
              />
            ))}
            {USER_TAB_IDS.flatMap((tab) =>
              ((PAGE_REGISTRY[tab] as { legacyPaths?: readonly string[] }).legacyPaths ?? []).map((legacyPath: string) => (
                <Route
                  key={`legacy-user-${tab}-${legacyPath}`}
                  path={legacyPath}
                  element={<Navigate to={PAGE_REGISTRY[tab].path} replace />}
                />
              ))
            )}
            {LEADERSHIP_TAB_IDS.map((tab) => (
              <Route
                key={`legacy-${tab}`}
                path={`/${tab}`}
                element={<Navigate to={PAGE_REGISTRY[tab].path} replace />}
              />
            ))}
            <Route path="/home" element={<Navigate to={PAGE_REGISTRY.dashboard.path} replace />} />
            <Route path="/leadership-hub" element={<Navigate to={PAGE_REGISTRY.dashboard.path} replace />} />
            <Route path="/dogfooding/*" element={<MainLayout initialTab="dogfooding" />} />
            <Route path="/chargebacks" element={<MainLayout initialTab="chargebacks" />} />
            <Route path="/pricing-optimizer" element={<MainLayout initialTab="pricing-optimizer" />} />
            <Route path="/inventory-audit" element={<MainLayout initialTab="inventory-audit" />} />
            <Route path="/3pl-audit" element={<Navigate to="/3pl-audit/historical" replace />} />
            <Route path="/3pl-audit/historical" element={<MainLayout initialTab="3pl-audit" />} />
            <Route path="/3pl-audit/live" element={<MainLayout initialTab="3pl-audit" />} />
            <Route path="/cx-home" element={<MainLayout initialTab="cx-home" />} />
            <Route path={PAGE_REGISTRY.github.path} element={<MainLayout initialTab="github" />} />
            <Route path={PAGE_REGISTRY.agents.path} element={<MainLayout initialTab="agents" />} />
            <Route path={PAGE_REGISTRY.skills.path} element={<MainLayout initialTab="skills" />} />
            <Route path={PAGE_REGISTRY['mill-deployments'].path} element={<MainLayout initialTab="mill-deployments" />} />
            <Route path={PAGE_REGISTRY['ai-os'].path} element={<MainLayout initialTab="ai-os" />} />
            <Route path={PAGE_REGISTRY['notion-ai-tasks'].path} element={<MainLayout initialTab="notion-ai-tasks" />} />
            <Route path={PAGE_REGISTRY['transformation-dashboard'].path} element={<MainLayout initialTab="transformation-dashboard" />} />
            <Route path="/github" element={<Navigate to={PAGE_REGISTRY.github.path} replace />} />
            <Route path="/settings" element={<SettingsScreen />} />
            <Route path="/settings/:tab" element={<SettingsScreen />} />
            <Route path={PAGE_REGISTRY['target-tna'].path} element={<MainLayout initialTab="target-tna" />} />
            <Route path={PAGE_REGISTRY['production-wip'].path} element={<MainLayout initialTab="production-wip" />} />
            <Route path="/product/sage-data" element={<Navigate to={PAGE_REGISTRY['production-wip'].path} replace />} />
            <Route path="/product/:productName" element={<MainLayout initialTab="okendo" />} />
            <Route path="/category/:categoryName" element={<MainLayout initialTab="okendo" />} />
            <Route path="*" element={<Navigate to={PAGE_REGISTRY.dashboard.path} replace />} />
          </Routes>
          <ToastContainer />
          </TooltipProvider>
        </AuthGuard>
    </>
  );
}

export default App;
